// Contract-period resolution for the UI.
//
// This is a deliberate mirror of backend services/productCatalog.js
// `resolveContractEnd`, and the two must agree. The backend stays the
// authority — it recomputes on create and its answer is what gets stored —
// but the offer → policy panel SHOWS the period to the user for
// confirmation and then submits it, so a panel that guesses differently
// silently overrides the tenant's convention.
//
// That is exactly what happened. The convert panel hardcoded
// `start + 1 year`, which for ARGOS (defaultContractExpiry '12-31', every
// contract running to 31 December) produced a policy ending on the
// inception anniversary instead. Because the panel submitted the value,
// the backend's `if (!create.endDate)` branch never ran and the correct
// rule never got a chance to apply.
//
// Keep the two implementations in step; tenantContractDates.test.mjs
// asserts they agree across a table of cases including the ARGOS one.

/**
 * The contract end for a given inception under a tenant's convention.
 *
 * @param {string|Date} startDate  coverage inception
 * @param {object} tenantConfig    the tenant config (reads defaultContractExpiry)
 * @param {object} [opts]
 * @param {number} [opts.termYears] contract duration where the product offers a choice
 * @returns {Date|null}
 */
export function resolveContractEnd(startDate, tenantConfig, opts = {}) {
  if (!startDate) return null;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;

  const rawTerm = Number(opts?.termYears);
  const termYears = Number.isFinite(rawTerm) && rawTerm >= 1 ? Math.floor(rawTerm) : 1;
  const extraYears = termYears - 1;

  const raw = String(tenantConfig?.defaultContractExpiry || '').trim();
  const match = /^(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (match) {
    const month = Number(match[1]);
    const day = Number(match[2]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const at = (year) => {
        const d = new Date(Date.UTC(year, month - 1, day));
        // A day that does not exist in the month (02-31) rolls forward;
        // pull it back to the last valid day of the intended month.
        if (d.getUTCMonth() !== month - 1) d.setUTCDate(0);
        return d;
      };
      // The first expiry after inception: this calendar day if it is still
      // ahead of inception, otherwise next year's.
      let end = at(start.getUTCFullYear());
      if (end.getTime() <= start.getTime()) end = at(start.getUTCFullYear() + 1);
      if (extraYears > 0) end = at(end.getUTCFullYear() + extraYears);
      return end;
    }
  }

  // No fixed expiry day configured: inception + term.
  const y = start.getUTCFullYear();
  const m = start.getUTCMonth();
  const d = start.getUTCDate();
  const next = new Date(Date.UTC(y + termYears, m, d));
  if (next.getUTCMonth() !== m) next.setUTCDate(0);   // 29 Feb → 28 Feb
  return next;
}

/** yyyy-mm-dd for an <input type="date">, or '' when there is no date. */
export function toDateInput(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * The coverage period a policy should inherit from the offer it converts.
 *
 * Order matters and is the whole point:
 *   1. the offer's PERSISTED coverage — what the client was actually shown
 *      on the generated document, including any amendment
 *   2. the offer's inception, with the end resolved from tenant convention
 *
 * The offer's `expiresAt` is QUOTE VALIDITY — the acceptance deadline — and
 * must never become a coverage date. It is not read here at all.
 */
export function policyPeriodFromOffer(offer, tenantConfig) {
  const persistedStart = offer?.coverage?.startDate;
  const inception = persistedStart || offer?.params?.productConfig?.effectiveDate || null;
  const start = toDateInput(inception);
  if (!start) return { start: '', end: '' };

  const persistedEnd = offer?.coverage?.endDate;
  if (persistedEnd) return { start, end: toDateInput(persistedEnd) };

  const termYears = offer?.coverage?.termYears
    ?? offer?.params?.productConfig?.contractTermYears;
  return { start, end: toDateInput(resolveContractEnd(start, tenantConfig, { termYears })) };
}
